import { useMemo } from 'react'

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { leadApi, type GetLeadsParams, type GetLeadsResponse } from 'services/leadApi'

import { leadQueryKeys } from './leadQueryKeys'

type UseLeadsQueryOptions = Omit<UseQueryOptions<GetLeadsResponse>, 'queryKey' | 'queryFn'>

// VERA-914 / VRG-105 (Stage B): mirrors useOwnersQuery — react-query is the
// established lighter-weight fetch pattern for this domain (vs. the redux-saga
// stack the existing OwnersPage table uses, which is left untouched).
export const useLeadsQuery = (params: GetLeadsParams = {}, options?: UseLeadsQueryOptions) => {
  // Create stable params object to prevent unnecessary refetches
  const stableParams = useMemo(() => params, [JSON.stringify(params)])

  const query = useQuery({
    queryKey: leadQueryKeys.list(stableParams),
    queryFn: () => leadApi.getLeads(stableParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    ...options,
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  }
}
